import Icon, { IconType } from "@edaz/edazkit/Icon";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import React from "react";
import Autosuggest, { RenderSuggestionsContainerParams } from "react-autosuggest";
import "./autosuggest.css";
import styles from "./tradingPlacesAutosuggest.module.scss";
import TradingPlacesViewModel from "viewModels/tradingPlaces/tradingPlacesViewModel";
import TradingPlacesFilter from "./tradingPlacesFilter";
import classNames from "classnames";
import TradingPlaceResponse from "entities/tradingPlaceResponse";
import TradingPlaceType from "entities/tradingPlaceType";

const getSuggestionValue = (suggestion: TradingPlaceResponse) => suggestion.name || "";

const renderSuggestion = (place: TradingPlaceResponse) => {
  let icon;
  if (place.type === TradingPlaceType.Kiosk) {
    icon = IconType.KioskOpen;
  } else {
    icon = IconType.PlaceOpen;
  }

  return (
    <div className={styles.suggestion}>
      <Icon icon={icon} className={styles.suggestionIcon} />
      <span className={classNames("ml-2", styles.suggestionLabel)}>{place.name || `${place.latitude}, ${place.longitude}`}</span>
    </div>
  );
};

export interface TradingPlacesAutosuggestProps {
  placeholder: string;
  notFoundMessage: string;
  vm: TradingPlacesViewModel;
  mobileView?: boolean;
}

const TradingPlacesAutosuggest = observer(({ vm, notFoundMessage, placeholder, mobileView }: TradingPlacesAutosuggestProps) => {
  const onChange = (event: any, { newValue }: { newValue: string }) => {
    vm.searchValue = newValue;
  };

  const onSuggestionsFetchRequested = async ({ value }: { value: string }) => {
    await vm.debouncedSearchTradingPlaces(value);
  };

  const onSuggestionsClearRequested = () => {
    vm.suggestedPlaces = [];
  };

  const onSuggestionSelected = (event: any, data: any) => {
    vm.tradingPlaceSelected(data.suggestion);
  };

  const renderSuggestionsContainer = (params: RenderSuggestionsContainerParams) => {
    return params.query && vm.suggestedPlaces ? (
      <div {...params.containerProps} className="react-autosuggest__suggestions-container--open">
        {params.children ?? <div className="react-autosuggest__container__not-found-message">{notFoundMessage}</div>}
      </div>
    ) : undefined;
  };

  const suggestions = toJS(vm.suggestedPlaces);

  const isAnyAutosuggestInputOpen = (suggestions && suggestions.length > 0) || vm.searchValue;
  const inputProps = {
    placeholder: placeholder,
    value: vm.searchValue,
    onChange: onChange,
    className: `form-control react-autosuggest__input ${isAnyAutosuggestInputOpen ? "react-autosuggest__input--open" : ""}`,
  };

  const renderInputComponent = (props: any) => {
    return (
      <>
        <input {...props} />
        <Icon icon={IconType.Search} width={20} height={20} className={styles.mapSearchIcon} />
      </>
    );
  };
  const showTradingPlaceFilter = suggestions.length === 0 && vm.searchValue.length === 0 && !mobileView;

  return (
    <div className={styles.filterContainer}>
      <Autosuggest
        suggestions={suggestions ?? []}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        inputProps={inputProps}
      />
      {showTradingPlaceFilter && <TradingPlacesFilter vm={vm} />}
    </div>
  );
});

export default TradingPlacesAutosuggest;
